import React, { createContext, useContext, useState, useCallback, forwardRef } from 'react';
import { motion, MotionConfig } from 'motion/react';

const HoverSliderContext = createContext(undefined);

export function useHoverSliderContext() {
  const context = useContext(HoverSliderContext);
  if (context === undefined) {
    throw new Error('useHoverSliderContext must be used within a HoverSliderProvider');
  }
  return context;
}

export const HoverSlider = forwardRef(({ children, className, style, ...props }, ref) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const changeSlide = useCallback((index) => setActiveSlide(index), []);
  return (
    <HoverSliderContext.Provider value={{ activeSlide, changeSlide }}>
      <div className={className} style={style} ref={ref} {...props}>
        {children}
      </div>
    </HoverSliderContext.Provider>
  );
});
HoverSlider.displayName = 'HoverSlider';

function splitText(text) {
  const words = text.split(' ').map((word) => word.concat(' '));
  const characters = words.map((word) => word.split('')).flat(1);
  return { words, characters };
}

export const TextStaggerHover = forwardRef(({ text, index, className, style, ...props }, ref) => {
  const { activeSlide, changeSlide } = useHoverSliderContext();
  const { characters } = splitText(text);
  const isActive = activeSlide === index;
  const handleMouse = () => changeSlide(index);

  return (
    <span
      className={className}
      style={{
        position: 'relative',
        display: 'inline-block',
        overflow: 'hidden',
        cursor: 'pointer',
        transformOrigin: 'bottom',
        ...style,
      }}
      {...props}
      ref={ref}
      onMouseEnter={handleMouse}
    >
      {characters.map((char, i) => (
        <span key={`${char}-${i}`} style={{ position: 'relative', display: 'inline-block', overflow: 'hidden' }}>
          <MotionConfig
            transition={{
              delay: i * 0.025,
              duration: 0.3,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          >
            <motion.span
              style={{ display: 'inline-block', opacity: isActive ? 0 : 0.2, transition: 'opacity 0.2s ease' }}
              initial={{ y: '0%' }}
              animate={isActive ? { y: '-110%' } : { y: '0%' }}
            >
              {char}
              {char === ' ' && i < characters.length - 1 && <>&nbsp;</>}
            </motion.span>

            <motion.span
              style={{ position: 'absolute', left: 0, top: 0, display: 'inline-block', opacity: 1 }}
              initial={{ y: '110%' }}
              animate={isActive ? { y: '0%' } : { y: '110%' }}
            >
              {char}
            </motion.span>
          </MotionConfig>
        </span>
      ))}
    </span>
  );
});
TextStaggerHover.displayName = 'TextStaggerHover';

export const clipPathVariants = {
  visible: {
    clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)',
  },
  hidden: {
    clipPath: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
  },
};

export const HoverSliderContentWrap = forwardRef(({ className, style, children, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={className}
      style={{
        display: 'grid',
        overflow: 'hidden',
        ...style,
      }}
      {...props}
    >
      {React.Children.map(children, (child) => (
        <div style={{ gridColumn: '1 / -1', gridRow: '1 / -1', width: '100%', height: '100%' }}>
          {child}
        </div>
      ))}
    </div>
  );
});
HoverSliderContentWrap.displayName = 'HoverSliderContentWrap';

export const HoverSliderContent = forwardRef(({ index, children, className, style, ...props }, ref) => {
  const { activeSlide } = useHoverSliderContext();
  return (
    <motion.div
      className={className}
      style={{ width: '100%', height: '100%', ...style }}
      transition={{ ease: [0.33, 1, 0.68, 1], duration: 0.8 }}
      variants={clipPathVariants}
      initial="hidden"
      animate={activeSlide === index ? 'visible' : 'hidden'}
      ref={ref}
      {...props}
    >
      {children}
    </motion.div>
  );
});
HoverSliderContent.displayName = 'HoverSliderContent';
